/**
 * Phase: "selection" — shows the map assigned to the selected robot and lets
 * the user choose up to MAX_STOPS delivery locations from that map's
 * allocated points. Emits onSubmit once at least one destination is set;
 * any unfilled stop row is discarded automatically on dispatch.
 *
 * Stop mutators are portal's append/remove/update/clear API
 * (onAppendStop/onRemoveStop/onUpdateStop/onClearStops), not index-addressed.
 */
import React from "react";
import {
  Box,
  Stack,
  Avatar,
  Typography,
  Button,
  Select,
  MenuItem,
  IconButton,
} from "@mui/material";
import MapIcon from "@mui/icons-material/Map";
import NavigationIcon from "@mui/icons-material/Navigation";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import RouteIcon from "@mui/icons-material/Route";
import ScheduleIcon from "@mui/icons-material/Schedule";
import PropTypes from "prop-types";

import DeliveryLayout from "../../../components/delivery/DeliveryLayout";
import SectionCard from "../../../components/delivery/SectionCard";
import ActionButtonRow from "../../../components/delivery/ActionButtonRow";
import DoorControl from "../../../components/delivery/DoorControl";
import { ChargingBlockedDialog } from "../../../components/delivery/ChargingBlockedDialog";
import { getStopLabel, MAX_STOPS, STOP_ETA_MINUTES } from "../deliveryConstants";
import { actionBtnSx, getSelectSx, getDividerSx, getSectionBoxSx } from "../deliveryStyles";
import { tokens } from "../../../theme";

/**
 * Can this delivery be dispatched?
 *  'charging'   — robot is on the charger; dispatch is hard-blocked
 *  'blocked'    — robot picked but unreachable; it can't receive nav commands
 *  'incomplete' — map or destinations still missing
 *  'ready'      — good to go
 *
 * Charging is checked FIRST: a docked robot can be perfectly reachable, so the
 * connection test would pass it straight through to 'ready'.
 */
const readinessState = ({
  hasSelectedRobot,
  isRobotConnected,
  isRobotCharging,
  selectedMap,
  allSelected,
}) => {
  if (hasSelectedRobot && isRobotCharging) return "charging";
  if (hasSelectedRobot && !isRobotConnected) return "blocked";
  if (!selectedMap || !allSelected) return "incomplete";
  return "ready";
};

// Status line under the destination picker: guides the user to the next
// missing step, or confirms what will be dispatched once everything is set.
const readinessLabel = (
  state,
  stops,
  selectedMap,
  { hasSelectedRobot, needsManualMap, mapsAndIds } = {}
) => {
  if (state === "charging") return "Robot is charging — dispatch unavailable";
  if (state === "blocked") return "Robot disconnected — waiting for it to reconnect";
  if (state === "incomplete") {
    if (selectedMap) return "Select at least one destination";
    if (!hasSelectedRobot) return "Select a robot to load its assigned map";
    if (!needsManualMap) return "Loading the robot's assigned map…";
    return mapsAndIds?.length
      ? "No map assigned to this robot — choose one to continue"
      : "No map assigned to this robot, and no maps are available to choose";
  }
  const filled = stops.filter((s) => s.name && s.name !== "");
  return filled.length === 1 ? `Ready: ${filled[0].name}` : `Ready: ${filled.length} stops`;
};

// Helper text under Step 1: explains where the assigned map came from, or why
// the manual picker is showing instead.
const assignedMapHelperText = (needsManualMap, mapsAndIds) => {
  if (!needsManualMap) return "Loaded automatically from the selected robot";
  if (mapsAndIds?.length) return "This robot has no assigned map — choose one for this delivery";
  return "This robot has no assigned map, and no maps are available";
};

// Colour tokens per readiness state.
const READINESS_TONES = {
  ready: {
    bg: tokens.successSoft,
    border: tokens.success,
    text: tokens.success,
    dot: tokens.success,
  },
  blocked: {
    bg: tokens.dangerSoft,
    border: tokens.danger,
    text: tokens.danger,
    dot: tokens.danger,
  },
  charging: {
    bg: tokens.warningSoft,
    border: tokens.warning,
    text: tokens.warning,
    dot: tokens.warning,
  },
  incomplete: { bg: tokens.surface2, border: tokens.border, text: tokens.text2, dot: tokens.text3 },
};

/** Status strip under the destination list — green when ready, red when blocked. */
const ReadinessRow = ({ state, label }) => {
  const tone = READINESS_TONES[state] || READINESS_TONES.incomplete;
  return (
    <Box
      sx={{
        p: 1.5,
        background: tone.bg,
        border: `1px solid ${tone.border}`,
        borderRadius: "10px",
      }}
    >
      <Stack direction="row" alignItems="center" gap={1}>
        <FiberManualRecordIcon sx={{ fontSize: 10, color: tone.dot }} />
        <Typography sx={{ fontSize: 12, fontWeight: 500, color: tone.text }}>{label}</Typography>
      </Stack>
    </Box>
  );
};

ReadinessRow.propTypes = { state: PropTypes.string, label: PropTypes.string };

/**
 * Read-only display of the map assigned to the selected robot — the normal case
 * now that maps are assigned per robot rather than picked per delivery.
 */
const AssignedMapRow = ({ robot, selectedMap }) => {
  const empty = !robot || !selectedMap;
  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={1}
      sx={{
        px: 1.5,
        py: 1,
        minHeight: 40,
        width: 220,
        boxSizing: "border-box",
        background: empty ? tokens.surface3 : tokens.accentSoft,
        border: `1px solid ${empty ? tokens.border : tokens.accent}`,
        borderRadius: "10px",
      }}
    >
      <MapIcon sx={{ fontSize: 16, flexShrink: 0, color: empty ? tokens.text3 : tokens.accent }} />
      <Typography
        noWrap
        title={selectedMap?.name || ""}
        sx={{
          fontSize: 13,
          fontWeight: empty ? 400 : 600,
          color: empty ? tokens.text3 : tokens.text,
        }}
      >
        {robot ? selectedMap?.name || "Loading assigned map…" : "Select a robot first"}
      </Typography>
    </Stack>
  );
};

AssignedMapRow.propTypes = { robot: PropTypes.object, selectedMap: PropTypes.object };

const SelectionPhase = ({
  /* robot (forwarded to DeliveryLayout's header) */
  portalRobots,
  onRobotChange,
  /* map state */
  selectedMap,
  mapsAndIds,
  onMapChange,
  needsManualMap = false,
  /* stop state */
  stops,
  mapLocations,
  onAppendStop,
  onRemoveStop,
  onUpdateStop,
  onClearStops,
  /* door */
  isDoorOpen,
  onDoorPress,
  isAeroRobot,
  /* layout */
  navigationTarget,
  previewLocations,
  robot,
  fromRobotId,
  batteryLevel,
  batteryCharging,
  showMapSelectionModalDialog,
  closeMapSelectionDialogModal,
  onEmergencyResume,
  isRobotConnected,
  /* submit */
  onSubmit,
}) => {
  const hasSelectedRobot = Boolean(robot);
  const isRobotCharging = Boolean(batteryCharging);
  // Unfilled rows are discarded on dispatch, so readiness only needs one
  // filled stop rather than every row being filled.
  const allSelected = stops.some((s) => s.name && s.name !== "");
  const readiness = readinessState({
    hasSelectedRobot,
    isRobotConnected: isRobotConnected !== false,
    isRobotCharging,
    selectedMap,
    allSelected,
  });

  // Surface the block the moment a charging robot is picked, rather than waiting
  // for the operator to work out why Start Navigation is greyed out. Re-opens if
  // they dismiss it and switch to another charging robot.
  const [chargingDialogOpen, setChargingDialogOpen] = React.useState(false);
  React.useEffect(() => {
    if (hasSelectedRobot && isRobotCharging) setChargingDialogOpen(true);
  }, [hasSelectedRobot, isRobotCharging, robot?.serialID]);

  return (
    <>
      <DeliveryLayout
        selectedMap={selectedMap}
        navigationTarget={navigationTarget}
        previewLocations={previewLocations}
        robot={robot}
        fromRobotId={fromRobotId}
        batteryLevel={batteryLevel}
        batteryCharging={batteryCharging}
        showMapSelectionModalDialog={showMapSelectionModalDialog}
        closeMapSelectionDialogModal={closeMapSelectionDialogModal}
        portalRobots={portalRobots}
        onRobotChange={onRobotChange}
        onEmergencyResume={onEmergencyResume}
        isRobotConnected={isRobotConnected}
        rightPanel={
          <SectionCard
            icon={
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "10px",
                  background: "rgba(255,255,255,0.15)",
                }}
              >
                <MapIcon sx={{ fontSize: 20, color: "#fff" }} />
              </Avatar>
            }
            title="Delivery Route"
            subtitle="Select map and delivery locations"
            headerBg={`linear-gradient(135deg, ${tokens.accent} 0%, ${tokens.accent2} 100%)`}
            headerDirection="row"
          >
            {/* Door open/close toggle */}
            <DoorControl
              isDoorOpen={isDoorOpen}
              onDoorPress={onDoorPress}
              isAeroRobot={isAeroRobot}
            />
            {isAeroRobot && <Box sx={getDividerSx()} />}

            {/* ── Step 1: The robot's assigned map ── */}
            <Box sx={getSectionBoxSx()}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: tokens.text, mb: 0.5 }}>
                Step 1: Assigned Map
              </Typography>
              <Typography sx={{ fontSize: 11, color: tokens.text3, mb: 1.25 }}>
                {assignedMapHelperText(needsManualMap, mapsAndIds)}
              </Typography>
              {needsManualMap ? (
                <Select
                  size="small"
                  displayEmpty
                  value={selectedMap?._id || ""}
                  onChange={(e) => onMapChange?.(e.target.value)}
                  sx={{ ...getSelectSx(), width: 220 }}
                >
                  <MenuItem value="" sx={{ fontSize: 13, color: tokens.text3 }}>
                    — Choose a map —
                  </MenuItem>
                  {mapsAndIds?.map((map) => (
                    <MenuItem key={map._id} value={map._id} sx={{ fontSize: 13 }}>
                      {map.name}
                    </MenuItem>
                  ))}
                </Select>
              ) : (
                <AssignedMapRow robot={robot} selectedMap={selectedMap} />
              )}
            </Box>

            {/* ── Step 2: Delivery stops ── */}
            {selectedMap && (
              <Box sx={getSectionBoxSx()}>
                <Stack direction="row" alignItems="baseline" justifyContent="space-between" gap={1}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: tokens.text, mb: 0.5 }}>
                    Delivery Route
                  </Typography>
                  {stops.some((s) => s.name) && (
                    <Button
                      size="small"
                      onClick={onClearStops}
                      startIcon={<ClearAllIcon sx={{ fontSize: "13px !important" }} />}
                      sx={{
                        minWidth: 0,
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: 11,
                        color: tokens.text3,
                        "&:hover": { color: tokens.danger, background: tokens.dangerSoft },
                      }}
                    >
                      Clear
                    </Button>
                  )}
                </Stack>
                <Typography sx={{ fontSize: 11, color: tokens.text3, mb: 1.25 }}>
                  Add up to {MAX_STOPS} delivery stops
                </Typography>
                <Stack gap={1} mb={1.25}>
                  {stops.map((stop, i) => (
                    <Stack key={stop.id} direction="row" alignItems="center" gap={1}>
                      {/* Stop label avatar */}
                      <Avatar
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: "8px",
                          background: tokens.accent,
                          color: "#fff",
                          fontFamily: "'JetBrains Mono', monospace",
                          fontSize: 12,
                          fontWeight: 700,
                          flexShrink: 0,
                        }}
                      >
                        {getStopLabel(i)}
                      </Avatar>
                      <Select
                        size="small"
                        displayEmpty
                        value={stop.name}
                        onChange={(e) => onUpdateStop(i, e.target.value)}
                        sx={{
                          ...getSelectSx(),
                          flex: 1,
                          "& .MuiSelect-select": { py: "10px !important" },
                        }}
                      >
                        <MenuItem value="" sx={{ fontSize: 13, color: tokens.text3 }}>
                          Choose destination
                        </MenuItem>
                        {mapLocations?.map((loc) => (
                          <MenuItem key={loc.name} value={loc.name} sx={{ fontSize: 13 }}>
                            {loc.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {/* Remove stop button */}
                      <IconButton
                        size="small"
                        onClick={() => onRemoveStop(i)}
                        disabled={stops.length === 1}
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: "8px",
                          background: tokens.dangerSoft,
                          color: tokens.danger,
                          flexShrink: 0,
                          "&:hover": { background: tokens.dangerSoft },
                          "&:disabled": { opacity: 0.35 },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 13 }} />
                      </IconButton>
                    </Stack>
                  ))}
                </Stack>

                {/* Add stop button (hidden when max reached) */}
                {stops.length < MAX_STOPS && (
                  <Button
                    fullWidth
                    size="small"
                    startIcon={<AddIcon sx={{ fontSize: "14px !important" }} />}
                    onClick={onAppendStop}
                    sx={{
                      borderRadius: "10px",
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: 12.5,
                      py: 1,
                      color: tokens.accent,
                      background: tokens.surface,
                      border: `1.5px dashed ${tokens.accent}`,
                      "&:hover": { background: tokens.accentSoft },
                      boxShadow: "none",
                    }}
                  >
                    Add another stop
                  </Button>
                )}
              </Box>
            )}

            {/* ── Route summary: stop count + rough ETA ── */}
            {allSelected && (
              <Stack
                direction="row"
                alignItems="center"
                gap={1.5}
                sx={{
                  px: 1.75,
                  py: 1.1,
                  background: tokens.accentSoft,
                  border: `1px solid ${tokens.accent}`,
                  borderRadius: "10px",
                }}
              >
                <RouteIcon sx={{ fontSize: 16, color: tokens.accent }} />
                <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: tokens.text }}>
                  {stops.filter((s) => s.name).length}{" "}
                  {stops.filter((s) => s.name).length === 1 ? "stop" : "stops"}
                </Typography>
                <Box sx={{ width: "1px", height: 14, background: tokens.accent, opacity: 0.4 }} />
                <ScheduleIcon sx={{ fontSize: 15, color: tokens.accent }} />
                <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: tokens.text }}>
                  Approx. {stops.filter((s) => s.name).length * STOP_ETA_MINUTES} min
                </Typography>
              </Stack>
            )}

            {/* ── Readiness status indicator ── */}
            <ReadinessRow
              state={readiness}
              label={readinessLabel(readiness, stops, selectedMap, {
                hasSelectedRobot,
                needsManualMap,
                mapsAndIds,
              })}
            />

            {/* ── Start Delivery ── */}
            <ActionButtonRow>
              <Button
                fullWidth
                variant="contained"
                disabled={readiness !== "ready"}
                onClick={onSubmit}
                startIcon={<NavigationIcon sx={{ fontSize: "18px !important" }} />}
                sx={{
                  ...actionBtnSx,
                  maxWidth: "100%",
                  py: 1.5,
                  fontSize: 14.5,
                  background: tokens.success,
                  color: "#fff",
                  "&:hover": { background: tokens.success, boxShadow: tokens.shadowLg },
                  "&:disabled": { opacity: 0.4 },
                }}
              >
                Start Delivery
              </Button>
            </ActionButtonRow>
          </SectionCard>
        }
      />

      {/* Hard block. The Start button is already disabled while charging, but
          a disabled button alone doesn't say WHY — this states the reason and
          has no override, so dispatch cannot proceed until the robot is off
          the charger. */}
      <ChargingBlockedDialog
        open={chargingDialogOpen}
        onClose={() => setChargingDialogOpen(false)}
        actionLabel="dispatched"
      />
    </>
  );
};

SelectionPhase.propTypes = {
  portalRobots: PropTypes.array,
  onRobotChange: PropTypes.func,
  selectedMap: PropTypes.object,
  mapsAndIds: PropTypes.array,
  onMapChange: PropTypes.func,
  /** True only when the robot has no usable assigned map — reveals the manual picker. */
  needsManualMap: PropTypes.bool,
  /** Ordered delivery rows; each carries a stable `id` so a removed or
   *  reordered row does not re-key the whole list. */
  stops: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string.isRequired, name: PropTypes.string })
  ),
  mapLocations: PropTypes.array,
  onAppendStop: PropTypes.func,
  onRemoveStop: PropTypes.func,
  onUpdateStop: PropTypes.func,
  onClearStops: PropTypes.func,
  isDoorOpen: PropTypes.bool,
  onDoorPress: PropTypes.func,
  isAeroRobot: PropTypes.bool,
  navigationTarget: PropTypes.object,
  previewLocations: PropTypes.array,
  robot: PropTypes.object,
  fromRobotId: PropTypes.string,
  batteryLevel: PropTypes.number,
  /** Hard-blocks dispatch while the robot is on the charger. */
  batteryCharging: PropTypes.bool,
  showMapSelectionModalDialog: PropTypes.bool,
  closeMapSelectionDialogModal: PropTypes.func,
  onEmergencyResume: PropTypes.func,
  isRobotConnected: PropTypes.bool,
  onSubmit: PropTypes.func,
};

export default SelectionPhase;
